/**
 * Cliente HTTP del Programa guiado para `apps/speak` (fase 15).
 *
 * Consume `GET /api/modules/english/program` → snapshot con las units, su
 * estado (incluido "a reforzar"), la recomendación y los contadores. Los tipos
 * se redefinen acá (UI-facing) en lugar de importar de `@orborix/module-languages`
 * para que speak quede autocontenida (mismo criterio que `scenariosApi.ts`).
 *
 * `credentials: 'include'` — `/api` se sirve same-origin vía el proxy de Vite;
 * la cookie httpOnly de sesión viaja sola.
 */

import { apiFetch, createTypedApiClient } from '@orborix/auth-client';

const BASE = '/api/modules/english';

// Las 3 dimensiones medibles por texto (17.E — fluency/pronunciation se
// eliminaron del currículum). El backend nunca envía otras.
export type ProgramDimension = 'grammar' | 'vocabulary' | 'functional';

export type ProgramCefr = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export type ProgramUnitStatus =
  | 'locked'
  | 'available'
  | 'in_progress'
  | 'mastered'
  | 'skipped'
  | 'revisit_later';

export type SubUnitStatus = 'available' | 'in_progress' | 'completed';

/** Sub-unidad para el popover + anillo segmentado (fase 17). */
export interface SubUnitView {
  id: string;
  order: number;
  titleEs: string;
  titleEn: string;
  blurbEs: string;
  status: SubUnitStatus;
  objectivesMet: number;
  objectivesTotal: number;
  masteryScore: number;
  /** Las units legacy no tienen teoría → no se muestra el botón. */
  hasTheory: boolean;
  /**
   * Below the learner's level and untouched: shown as known ("Ya lo sabés"),
   * still practicable as a review. False once the learner works on it.
   */
  assumedKnown: boolean;
}

export interface ProgramUnitView {
  id: string;
  title: string;
  /** Título corto en español (fase 17). La UI lo prefiere sobre `title` inglés. */
  titleEs?: string;
  dimension: ProgramDimension;
  cefr: ProgramCefr;
  status: ProgramUnitStatus;
  sessionsCount: number;
  /** El user comete errores que mapean a esta unit (aunque no la haya practicado). */
  needsReinforcement: boolean;
  /** Peso de refuerzo crudo (0..1) — intensidad del acento "a reforzar". */
  reinforcementWeight: number;
  /**
   * "Assumed known" (placement credit): below the learner's level and with no
   * real progress. Never the suggested unit; still openable to review. The
   * backend computes it from the learner's level on every snapshot.
   */
  assumedKnown: boolean;
  /** 1-2 oraciones en español: por qué importa el capítulo (sheet de detalle). */
  rationale: string;
  /** Objetivos demostrables ("can …") — "vas a poder" del sheet de detalle. */
  objectives: string[];
  /** Dominio 0..1 (fase 16/17). */
  masteryScore: number;
  /** "X de Y": objetivos (legacy) o sub-unidades (autorada) demostrados / total. */
  criteriaMet: number;
  criteriaTotal: number;
  /** Total de sub-unidades (fase 17). Legacy = 1. Anillo segmentado. */
  subUnitsTotal: number;
  /** Sub-unidades completadas (fase 17). Segmentos verdes. */
  subUnitsCompleted: number;
  /** Sub-unidades autoradas (popover + reader). Ausente en units legacy. */
  subUnits?: SubUnitView[];
}

/** Idioma de la teoría: idioma aprendido (en) | nativo (es). */
export type TheoryLang = 'en' | 'es';

/** Teoría de una sub-unidad: matriz idioma × profundidad (markdown). */
export interface SubUnitTheory {
  simple: Record<TheoryLang, string>;
  detailed: Record<TheoryLang, string>;
}

// No `next_at_lower_level` any more: units below the learner's level are
// "assumed known" and the recommendation never walks down to them.
export type ProgramRecommendationReason =
  | 'last_in_progress'
  | 'reinforcement_weakness'
  | 'next_at_level'
  | 'all_completed';

/** Prueba concreta de una reco de refuerzo: error real del user (de sus charlas). */
export interface RecommendationEvidence {
  pattern: string;
  example: string | null;
}

export interface ProgramRecommendation {
  kind: 'unit' | 'family';
  reason: ProgramRecommendationReason;
  unit: ProgramUnitView;
  title: string;
  family?: { id: string; title: string; unitIds: readonly string[] };
  /** Errores reales que la justifican (vacío si no es por refuerzo). */
  evidence: RecommendationEvidence[];
}

export interface ProgramSnapshot {
  levels: Record<ProgramDimension, ProgramCefr>;
  dimensionEnabled: Record<ProgramDimension, boolean>;
  units: ProgramUnitView[];
  /** Recomendación primaria (compat). Igual a `recommendations[0]` o `null`. */
  recommendation: ProgramRecommendation | null;
  /** Hasta 2 recomendaciones para el bloque "Para vos" (Home + Debrief). */
  recommendations: ProgramRecommendation[];
  masteredCount: number;
  totalCount: number;
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'ProgramApiError',
  // Es el único wrapper de speak con `signal` que se envolvía como
  // `network_error`: sus dos callers (`useProgram`, `TheoryReader`) abortan al
  // desmontar, o sea que el `AbortError` es "cancelé yo" y no una caída de red.
  // Se re-lanza tal cual, igual que `costApi`/`sessionsApi`/`sessionSummaryApi`.
  abortError: 'rethrow',
});

export const ProgramApiError = client.ApiError;
export type ProgramApiError = InstanceType<typeof client.ApiError>;

const { get } = client;

/** Snapshot del programa del user (units + estado + recomendación). */
export function fetchProgram(signal?: AbortSignal): Promise<ProgramSnapshot> {
  return get<ProgramSnapshot>('/program', signal ? { signal } : {});
}

/**
 * Teoría de una sub-unidad (4 variantes idioma×profundidad, fase 17). Lazy: la
 * trae el reader al entrar. 404 → la unit es legacy o el subId no existe.
 */
export function fetchSubUnitTheory(
  unitId: string,
  subId: string,
  signal?: AbortSignal,
): Promise<SubUnitTheory> {
  const path = `/program/units/${encodeURIComponent(unitId)}/subunits/${encodeURIComponent(subId)}/theory`;
  return get<SubUnitTheory>(path, signal ? { signal } : {});
}

/** Mensaje en el historial del theory-chat. */
export interface TheoryChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Envía una pregunta al coach sobre la teoría activa.
 * Recibe el historial completo y devuelve la respuesta del asistente.
 */
/** Idioma en el que el coach del chat debe responder. */
export type TheoryChatReplyLang = 'en' | 'es';

export async function postTheoryChat(params: {
  unitId: string;
  subId: string;
  theoryMarkdown: string;
  messages: TheoryChatMessage[];
  /** Idioma de respuesta del coach (toggle). Default 'en' server-side. */
  replyLang?: TheoryChatReplyLang;
  /** Nivel CEFR del learner para calibrar la dificultad. */
  cefrLevel?: string;
}): Promise<string> {
  const res = await apiFetch(`${BASE}/theory-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `theory-chat: ${res.status}`);
  }
  const data = await res.json() as { answer: string };
  return data.answer;
}
